import { add } from 'date-fns';
import { isEmpty } from 'lodash-es';
import React, { memo } from 'react';
import PostType, { CampaignType } from '@/constants/postType.constants';
import useStore from '@/stores/store';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { Controller, useFormContext } from 'react-hook-form';
import PostViewersSelector from '@/components/PostViewerSelector/PostViewersSelector';
import PostViewersSelectorNekiAdmin from '@/components/PostViewerSelector/PostViewersSelectorNekiAdmin';
import Typography from '@/components/next-ui/Typography';
import { useIsCompanyAdmin, useIsNonProfitAdmin } from '@/hooks/useUser';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Input from '@ui/Input';
import TextArea from '@ui/TextArea';
import ImageCarousel from '../ImageCarousel';
import NonProfitSearchBox from './FundraisingOrgSearchBox';
import ProjectSelector from './ProjectSelector';
import OrgType from '@/constants/orgType.constant';
import PostNotify from '../PostNotify';
import FundraisingOrgSearchBox from './FundraisingOrgSearchBox';
import Switch from '@/components/next-ui/Switch';
import { getUserCountry } from '@/utils/user.helper';

function CreateCrowdFundingForm(props) {
  const { isEditMode, images, videos, isNekiAdmin } = props;
  const userProfile = useStore((state) => state.user.profile);
  const isNonProfitAdmin = useIsNonProfitAdmin();
  const isCompanyAdmin = useIsCompanyAdmin();

  const {
    formState: { errors },
    control,
    getValues,
    setValue,
    watch,
  } = useFormContext();

  const isOrganization =
    watch('beneficiary.beneficiaryType') === 'organization';
  const isUserInIndia = getUserCountry(userProfile) === 'IN';
  const isOrgConnectedToStripe = !!userProfile?.activeCommunity?.stripeId;
  const externalContributionLinkMessage = isUserInIndia
    ? 'Link to your fundraising page (e.g., Ketto)'
    : 'Link to your fundraising page (e.g., GoFundMe)';

  return (
    <div className="flex w-full flex-col gap-6 pb-4 pt-6">
      <div className="mb-1.5 ml-1.5">
        <PostViewersSelector disabled={isEditMode} />
      </div>

      {/* <div>
        <PostNotify
          postType={PostType.UserPost}
          orgId={userProfile?.activeCommunity.id}
          orgType={userProfile?.activeCommunity?.orgType}
        />
      </div> */}

      <div>
        <Typography>
          {isOrganization
            ? 'This fundraiser is for your organization'
            : 'This fundraiser is for an individual in need'}
          . Donation Match and tax benefits are not available.
        </Typography>
      </div>

      {/* {(isNekiAdmin || isCompanyAdmin) && (
        <Controller
          control={control}
          name="fundraisingOrg"
          render={({ field: { onChange, value } }) => (
            <FundraisingOrgSearchBox
              isNekiAdmin={isNekiAdmin}
              value={value}
              onChange={(val) => {
                onChange(val);
              }}
              error={errors.fundraisingOrg?.message}
            />
          )}
        />
      )} */}

      <div className="flex flex-col gap-6">
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <Input
              {...field}
              label="Title"
              placeholder="Title"
              error={errors.title?.message || ''}
              required
            />
          )}
        />

        <div className="align-center flex flex-col flex-nowrap justify-between gap-6 sm:flex-row sm:gap-2">
          <div className="w-full grow">
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextArea
                  {...field}
                  label="Description"
                  placeholder="Description"
                  rows={3}
                  className="w-full"
                  error={errors.description?.message}
                  required
                />
              )}
            />
          </div>
        </div>

        {/* <Controller
          name="benefactor.beneficiaryType"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch
              label="Crowd Fund for an Organization"
              checked={value === 'organization'}
              onChange={(state) =>
                onChange(state ? 'organization' : 'individual')
              }
            />
          )}
        /> */}

        {!isOrganization && (
          <>
            <Controller
              control={control}
              name="beneficiary.beneficiaryName"
              render={({ field }) => (
                <Input
                  {...field}
                  label="Beneficiary Name"
                  placeholder="Enter Beneficiary Name"
                  error={errors.beneficiary?.beneficiaryName?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="beneficiary.relationship"
              render={({ field }) => (
                <Input
                  {...field}
                  label="Beneficiary's Relationship to You"
                  placeholder="Enter Relationship"
                  error={errors.beneficiary?.relationship?.message}
                  required
                />
              )}
            />
          </>
        )}

        {!isOrgConnectedToStripe && (
          <Controller
            control={control}
            name="contributionUrl"
            render={({ field }) => (
              <Input
                {...field}
                label="External Contribution Link"
                placeholder={externalContributionLinkMessage}
                error={errors.contributionUrl?.message || ''}
                required
                // tooltip={externalContributionLinkMessage}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="goalAmount"
          render={({ field: { onChange, ...rest } }) => (
            <Input
              {...rest}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (!isNaN(val)) {
                  onChange(e.target.value);
                } else {
                  onChange('');
                }
              }}
              label="Goal"
              placeholder="The target amount to be raised"
              error={errors.goalAmount?.message || ''}
              adornment={
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className="font-medium">
                  {userProfile.location.currencySymbol}
                </Typography>
              }
              required
            />
          )}
        />
        <div className="flex w-full flex-col items-start gap-4 sm:flex-row">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { value, onChange } }) => (
                <MobileDatePicker
                  inputFormat="MMM dd, yyyy"
                  value={value}
                  onChange={onChange}
                  renderInput={({ inputProps, onClick }) => (
                    <Input
                      {...inputProps}
                      onClick={onClick}
                      value={value?.toDateString()}
                      label="Start Date"
                      required
                      tooltip="Launch date for when contributions start rolling in"
                      error={errors.startDate?.message}
                    />
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name="endDate"
              render={({ field: { value, onChange } }) => (
                <MobileDatePicker
                  inputFormat="MMM dd, yyyy"
                  value={value}
                  onChange={onChange}
                  renderInput={({ inputProps, onClick }) => (
                    <Input
                      {...inputProps}
                      onClick={onClick}
                      value={value?.toDateString()}
                      label="End Date"
                      required
                      tooltip={`Campaign closes after this date"`}
                      error={errors.endDate?.message}
                    />
                  )}
                />
              )}
            />
          </LocalizationProvider>
        </div>

        {isEmpty(images) && (
          <div>
            <ImageCarousel size="xs" images={images} disabled />
          </div>
        )}
        {isEmpty(videos) && (
          <div>
            <ImageCarousel type="video" size="xs" images={videos} disabled />
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateCrowdFundingForm;
